// Pure helpers for the Map page. Nothing here calls Google Maps, so the logic can be
// checked without a browser.

// Serbia: centre of the country, and the zoom level at which the whole country is visible
// on a map of ordinary size (about 1000 x 600 px).
export const SERBIA_CENTER = { lat: 44.0165, lng: 21.0059 }
export const SERBIA_BOUNDS = { south: 42.23, west: 18.81, north: 46.19, east: 23.01 }
// Used only when the size of the map is not known yet
export const SERBIA_ZOOM = 7

// The highest zoom level (Web Mercator, 256 px tiles) at which `bounds` still fits into a map
// of `width` x `height` px, keeping `padding` px free at every edge. Google Maps zoom levels are
// whole numbers, so the result is rounded down; it is kept between `min` and `max`.
export const zoomForBounds = (bounds, width, height, { padding = 24, min = 3, max = 12 } = {}) => {
  const mercatorY = (lat) => {
    const sin = Math.sin((lat * Math.PI) / 180)
    return Math.max(Math.min(Math.log((1 + sin) / (1 - sin)) / 2, Math.PI), -Math.PI) / 2
  }
  const zoomFor = (pixels, fraction) => Math.floor(Math.log(pixels / 256 / fraction) / Math.LN2)

  const latFraction = (mercatorY(bounds.north) - mercatorY(bounds.south)) / Math.PI
  const lngSpan = bounds.east - bounds.west
  const lngFraction = (lngSpan < 0 ? lngSpan + 360 : lngSpan) / 360

  const w = Math.max(width - 2 * padding, 1)
  const h = Math.max(height - 2 * padding, 1)
  const zoom = Math.min(zoomFor(h, latFraction), zoomFor(w, lngFraction))
  return Number.isFinite(zoom) ? Math.max(min, Math.min(max, zoom)) : SERBIA_ZOOM
}

const toNumber = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value))

// { lat, lng } of a location, or null when it has no usable coordinates (missing, not a
// number, out of range, or 0/0, which is what an empty geocoding result looks like).
export const locationPosition = (location) => {
  const lat = toNumber(location?.latitude)
  const lng = toNumber(location?.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

// The zoom at which the whole of Serbia fits into a map of `size` ({ width, height } in px)
export const zoomForSerbia = (size) =>
  size?.width && size?.height ? zoomForBounds(SERBIA_BOUNDS, size.width, size.height) : SERBIA_ZOOM

// The smallest box that contains all the pins ({ south, west, north, east }), or null when
// there are none. `groups` is the result of groupByPosition.
export const boundsOfGroups = (groups) => {
  if (!groups || groups.length === 0) return null
  const box = { south: 90, west: 180, north: -90, east: -180 }
  for (const { position } of groups) {
    box.south = Math.min(box.south, position.lat)
    box.north = Math.max(box.north, position.lat)
    box.west = Math.min(box.west, position.lng)
    box.east = Math.max(box.east, position.lng)
  }
  return box
}

// How far to zoom in on a set of pins: close enough to see them, but never closer than a
// city (a single pin, or pins that are all in one spot, would otherwise zoom in to a street).
const MAX_FIT_ZOOM = 12

// Where the map starts, from what is known (the same rule is used when the data arrives later):
// 1. the user's position, at the zoom that shows the whole of Serbia,
// 2. otherwise the pins: a view with all of them, centred on their box,
// 3. otherwise (no location at all) Serbia.
// `size` is { width, height } of the map in pixels. Returns { center, zoom, source }.
export const initialView = ({ userPosition, groups, size }) => {
  const width = size?.width
  const height = size?.height
  const serbiaZoom = zoomForSerbia(size)

  if (userPosition) {
    return { center: userPosition, zoom: serbiaZoom, source: "user" }
  }

  const box = boundsOfGroups(groups)
  if (box) {
    const center = { lat: (box.south + box.north) / 2, lng: (box.west + box.east) / 2 }
    // One place, or all in one spot: nothing to fit, a city-level view of it
    const single = box.north - box.south < 1e-4 && box.east - box.west < 1e-4
    const zoom = single || !width || !height
      ? Math.min(serbiaZoom + 4, MAX_FIT_ZOOM)
      : zoomForBounds(box, width, height, { max: MAX_FIT_ZOOM })
    return { center, zoom, source: "locations" }
  }

  return { center: SERBIA_CENTER, zoom: serbiaZoom, source: "serbia" }
}

// One map marker per distinct place: locations at the same coordinates (about 1 m) would sit
// exactly on top of each other and could never be told apart, so they share a marker.
// Returns [{ key, position, ids, names }]; locations without usable coordinates are left out.
export const groupByPosition = (locations) => {
  const groups = new Map()
  for (const location of locations || []) {
    const position = locationPosition(location)
    if (!position) continue
    const key = position.lat.toFixed(5) + ',' + position.lng.toFixed(5)
    if (!groups.has(key)) {
      groups.set(key, { key, position, ids: [], names: [] })
    }
    groups.get(key).ids.push(location.id)
    groups.get(key).names.push(location.name || '')
  }
  return [...groups.values()]
}

// Text that changes only when a marker is added, removed or moved, or the locations (or their
// names) at a marker change. The page keeps the same marker objects while it stays the same,
// so the map is not redrawn on every render.
export const groupsSignature = (groups) =>
  groups.map(g => g.key + '=' + g.ids.join('+') + ':' + g.names.join('+')).join('|')

// Number of LOCATIONS in a cluster (not of markers): a marker can stand for several
// locations, and carries their number in its `locationCount` property.
export const clusterTotal = (markers) =>
  (markers || []).reduce((sum, marker) => sum + (Number(marker.get('locationCount')) || 1), 0)

// Diameter of the round cluster symbol: bigger clusters get a bigger symbol.
export const clusterSize = (total) => (total < 10 ? 40 : total < 100 ? 48 : 56)

// SVG of the cluster symbol (a round badge in the brand colour). The number is drawn by the
// marker label, so the text stays sharp and is not scaled together with the image.
export const clusterSvg = (size) => {
  const half = size / 2
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<circle cx="${half}" cy="${half}" r="${half}" fill="#422AFB" fill-opacity="0.25"/>` +
    `<circle cx="${half}" cy="${half}" r="${half - 5}" fill="#422AFB" stroke="#ffffff" stroke-width="2"/>` +
    '</svg>'
  )
}

// "My location": zoom 17 (street level, about 0.85 m per pixel in Serbia). The clusterer joins
// nothing from zoom 16 on, so nearby locations are separate pins and one click shows their data.
export const ZOOM_ON_USER = 17

// Zoom limits of the wheel
export const MIN_WHEEL_ZOOM = 3
export const MAX_WHEEL_ZOOM = 20

// Mouse wheel: one zoom level per WHEEL_THRESHOLD of wheel travel (a notch of an ordinary mouse
// is about 100), and never more often than every WHEEL_MIN_INTERVAL ms. A touchpad sends many
// tiny events: they add up instead of each one zooming. A pause of WHEEL_IDLE ms forgets what
// was collected, so a stray event after a pause does not zoom.
export const WHEEL_THRESHOLD = 100
export const WHEEL_MIN_INTERVAL = 280
export const WHEEL_IDLE = 200

export const NEW_WHEEL_STATE = { sum: 0, lastEvent: -Infinity, lastStep: -Infinity }

// Travel of one wheel event in pixels (browsers may report lines or pages instead)
export const wheelDelta = (event) =>
  (Number(event.deltaY) || 0) * (event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? 800 : 1)

// `state` is what this function returned last time (NEW_WHEEL_STATE at first). Returns the new
// state and `step`: +1 to zoom in, -1 to zoom out, 0 for nothing yet. Wheel down zooms out.
export const nextWheel = (state, delta, now) => {
  const carried = now - state.lastEvent > WHEEL_IDLE ? 0 : state.sum
  let sum = Math.max(-WHEEL_THRESHOLD, Math.min(WHEEL_THRESHOLD, carried + delta))
  let step = 0
  let lastStep = state.lastStep

  if (Math.abs(sum) >= WHEEL_THRESHOLD && now - state.lastStep >= WHEEL_MIN_INTERVAL) {
    step = sum > 0 ? -1 : 1
    sum = 0
    lastStep = now
  }
  return { state: { sum, lastEvent: now, lastStep }, step }
}

// Web Mercator on a world of size 1 x 1 (x from the west, y from the north)
const toWorld = ({ lat, lng }) => {
  const sin = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180)
  return { x: (lng + 180) / 360, y: 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI) }
}

const fromWorld = ({ x, y }) => {
  const lat = ((2 * Math.atan(Math.exp((0.5 - y) * 2 * Math.PI)) - Math.PI / 2) * 180) / Math.PI
  let lng = x * 360 - 180
  lng = ((((lng + 180) % 360) + 360) % 360) - 180
  return { lat: Math.max(-85, Math.min(85, lat)), lng }
}

// Where the centre of the map has to go when the zoom changes from `zoom` to `nextZoom`, so the
// point under the mouse stays under the mouse (zooming "into the cursor", not into the middle).
// `size` is the map in px, `cursor` the mouse position in px from the top left corner of the map.
export const centerAfterZoom = ({ center, zoom, nextZoom, size, cursor }) => {
  const scale = 256 * Math.pow(2, zoom)
  const nextScale = 256 * Math.pow(2, nextZoom)
  const middle = toWorld(center)
  const dx = cursor.x - size.width / 2
  const dy = cursor.y - size.height / 2
  const under = { x: middle.x + dx / scale, y: middle.y + dy / scale }
  return fromWorld({ x: under.x - dx / nextScale, y: under.y - dy / nextScale })
}

